import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QueryClientProvider } from '@tanstack/react-query';
import { translate } from '~l10n/client';
import Backbone from 'backbone';
import Provider from 'react-redux/es/components/Provider';
import _ from 'underscore';

import { AsyncDialogContext } from '~shared/context/AsyncDialogContext';
import {
  TeacherApplicationContext,
  TeacherApplicationContextProvider,
} from '~shared/context/TeacherApplicationContext';
import { PageNotFound } from '~teacher/components/NotFound';
import { RouterProvider } from '~teacher/context/RouterContext';
import { PersonalQuizPage } from '~teacher/pages/PersonalQuizPage';
import { queryClient } from '~teacher/queryClient';
import { loadQuizBySocNumber } from '~teacher/slices/quiz/quizAppThunks';
import { selectQuizBySocNumber } from '~teacher/slices/quiz/quizSelectors';
import {
  beginEditingQuiz,
  refreshEditingQuizQuestions,
  setLegacyStandards,
} from '~teacher/slices/quiz/quizSlice';
import Constants from 'Constants';
import model from 'EditQuizModel';
import header from 'HeaderController';
import teacher from 'TeacherModel';

import popup from '../../../shared/components/popup/PopupController';
import { ErrorView } from '../../shared/error/ErrorView';
import { personalQuizUrl as quizUrl } from '../../src/api/urls';

let deepCopy = require('rfdc')();

const reduxStore = require('../../src/store');

let router = null;

const showOutOfDateClientPopup = () => {
  popup.render({
    isModal: true,
    title: translate('Update Available'),
    message: translate(
      'A newer version of Socrative is available! Please refresh the page to get the latest updates.'
    ),
    buttonText: translate('Refresh'),
    buttonClicked: () => {
      window.location.reload();
    },
  });
};

function EditQuizController() {
  _.extend(this, Backbone.Events);
}

EditQuizController.prototype = {
  editQuizSetup: function (teacherRouter) {
    router = teacherRouter;

    let createQuizContainer = document.getElementById('main-content');
    ReactDOM.unmountComponentAtNode(createQuizContainer);

    header.render();
    model.reset();
    teacher.shouldFetchQuizzes = true;

    if (teacher.currentQuizFolderId === Constants.TRASH) {
      teacher.currentQuizFolderId = Constants.ROOT_FOLDER;
    }

    return createQuizContainer;
  },

  doEditQuiz: function (socNumber, teacherRouter) {
    let createQuizContainer = controller.editQuizSetup(teacherRouter);
    // This is a hacky way of showing that "Saved quiz banner" on the quizzes page.
    // the intention here, is to remove this, when the quizzes list page is updated.

    if (!createQuizContainer) {
      window.location.reload(); // Should we refresh?
    } else {
      reduxStore
        .dispatch(
          loadQuizBySocNumber({ socNumber: socNumber, quizUrl: quizUrl })
        )
        .then(() => {
          const state = reduxStore.getState();
          const { id, parentId } = selectQuizBySocNumber(state, {
            socNumber,
          });
          const quizData = state.teachers.quizData.quizzes[id];

          model.setUpStandard(quizData, controller.render);

          reduxStore.dispatch(
            beginEditingQuiz({
              quizId: id,
            })
          );

          ReactDOM.render(
            <TeacherApplicationContextProvider
              applicationContext={TeacherApplicationContext.Socrative}
            >
              <ErrorView>
                <Provider store={reduxStore}>
                  <QueryClientProvider client={queryClient}>
                    <RouterProvider router={router}>
                      <AsyncDialogContext>
                        <PersonalQuizPage
                          quizId={id}
                          parentFolderId={parentId}
                          controller={controller}
                          refreshQuestions={() =>
                            reduxStore
                              .dispatch(
                                loadQuizBySocNumber({
                                  socNumber: socNumber,
                                  quizUrl: quizUrl,
                                })
                              )
                              .then(() => {
                                reduxStore.dispatch(
                                  refreshEditingQuizQuestions({
                                    quizId: id,
                                  })
                                );
                              })
                          }
                        />
                      </AsyncDialogContext>
                    </RouterProvider>
                  </QueryClientProvider>
                </Provider>
              </ErrorView>
            </TeacherApplicationContextProvider>,
            document.getElementById('main-content')
          );
        })
        .catch((error) => {
          if (__DEV__) {
            console.error(error);
          }
          ReactDOM.render(
            <PageNotFound />,
            document.getElementById('main-content')
          );
        });
    }
  },

  render: function () {
    if (model.quiz.standard) {
      let standardClone = deepCopy(model.quiz.standard); //Need to deep copy the quiz standards because backbone model (editQuizModel) modifies in the background
      reduxStore.dispatch(setLegacyStandards(standardClone));
    }
    return;
  },

  alignToStandardChanged: function (checked) {
    if (checked) {
      model.addNewStandard();
    } else {
      delete model.quiz.standard;
    }

    model.quiz.set('dirty', true);
    controller.render();
  },

  standardChanged: function (changed, standard) {
    model.quiz.standard.standardsObject.noResponseData = null;
    model.setStandards(changed, standard);
  },

  showApiError: function (changed) {
    this.render(); // Show the loading indicator for the drop down that has no options.

    popup.render({
      title: translate('Try Again'),
      message: translate(
        'There are no options available. Please make another selection.'
      ),
      buttonClicked: () => {
        model.quiz.standard.standardsObject.noResponseData = changed;
        this.render();
      },
    });
  },

  editStandardClicked: function () {
    model.getStandardList(() => {
      model.quiz.standard.backup = _.clone(model.quiz.standard); // Clone the existing standard in case the teacher cancels.
      model.quiz.standard.dirty = true;
      model.quiz.set('dirty', true);
      let cloneStandard = deepCopy(model.quiz.standard);
      reduxStore.dispatch(setLegacyStandards({ quizStandard: cloneStandard }));
      controller.render();
    });
  },

  cancelEditStandardClicked: function () {
    model.quiz.standard = model.quiz.standard.backup;
    model.quiz.standard.backup = null;
    model.quiz.standard.standardsObject = _.clone(model.blueprint);
    model.quiz.standard.dirty = false;
    controller.render();
  },

  deleteStandardClicked: function () {
    popup.render({
      title: translate('Please Confirm'),
      buttonText: translate('Yes'),
      cancelText: translate('No'),
      message: translate('Are you sure you want to delete this standard?'),
      buttonClicked: () => {
        delete model.quiz.standard;
        model.quiz.set('dirty', true);

        controller.render();
      },
    });
  },

  deleteStandard: function () {
    delete model.quiz.standard;
    model.quiz.set('dirty', true);
    controller.render();
  },
};

let controller = new EditQuizController();

controller.listenTo(model, 'error:emptyApiResponse', controller.showApiError);
controller.listenTo(model, 'render', controller.render);

module.exports = controller;
